<template>
  <div>
    <h3 v-if="localValue.length > 0">
      {{ $t("templateDashboard.slots.select.title") }}
    </h3>

    <v-card v-for="(item, itemIndex) in localValue" :key="itemIndex">
      <v-card-text class="ma-2" v-if="item.index != -1">
        <v-autocomplete
          v-model="localValue[itemIndex].tagId"
          :items="selectableTags"
          :label="
            $t('templateDashboard.slots.select.deveui') + `: ${item?.index}`
          "
          item-text="name"
          item-value="deveui"
          dense
          :rules="[rules.name]"
          @change="loadKeys(item.tagId, $event)"
          :filter="tagFilter"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name" />
              <v-list-item-subtitle v-html="data.item.deveui" />
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <v-autocomplete
          v-for="slot in distinctSlots(item)"
          :key="slot.slotId"
          v-model="slot.key"
          :items="getKeys(item.tagId)"
          :label="slot.slotDescription ?? $t('tagdata.fields.key')"
          item-text="label"
          item-value="key"
          dense
          @change="setKeyForAllSlots(itemIndex, slot.slotIds, $event)"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title v-html="data.item.label" />
              <v-list-item-subtitle v-html="data.item.key" />
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-card-text>
    </v-card>
    <v-btn @click="$emit('updateDefaults')" v-if="updateable">
      {{ $t("common.updateDefault") }}
    </v-btn>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { autoCompleteTagFilter } from "@/_helpers/commonHelper";
import { cloneDeep } from "lodash-es";

export default {
  name: "TemplateDashboardSlotSelector",

  props: {
    value: {
      type: Array,
      default: () => [],
    },

    updateable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectableTags: [],
      selectableKeys: {},

      rules: {
        name: (v) => !!v || this.$t("validation.requiredSingle"),
      },
    };
  },

  computed: {
    localValue: {
      get() {
        return this.value;
      },

      set(val) {
        this.$emit("input", val);
      },
    },
  },

  methods: {
    ...mapActions("tag", ["getTagsWithInformation", "getKeysForTag"]),

    async loadKeys(deveui) {
      if (this.selectableKeys[deveui]) return;

      // Needs to cloneDeep for reactivity
      let temp = cloneDeep(this.selectableKeys);
      temp[deveui] = (await this.getKeysForTag({ tagId: deveui })) ?? [];
      this.selectableKeys = temp;
    },

    tagFilter(item, queryText) {
      return autoCompleteTagFilter(item, queryText);
    },

    getKeys(deveui) {
      return this.selectableKeys[deveui] ?? [];
    },

    // Due to slots being "per widget" and not "per templatedashboard" there could potentially be several slots that should act as the same.
    // This is distincted (indirectly by item.index) and directly by the slots slotDescription.
    distinctSlots(item) {
      let slots = [];
      for (var slotKey of item.slotKeys) {
        var slot = slots.find(
          (x) => x.slotDescription == slotKey.slotDescription
        );
        if (slot == undefined) {
          slots.push({ ...slotKey, slotIds: [slotKey.slotId] });
          continue;
        }

        slot.slotIds.push(slotKey.slotId);
      }
      return slots;
    },

    // Used to update all the slots that have been "combined" by the distinctSlots method.
    setKeyForAllSlots(itemIndex, slotIds, value) {
      for (var slotId of slotIds) {
        this.localValue[itemIndex].slotKeys.find(
          (x) => x.slotId == slotId
        ).key = value;
      }
    },
  },

  async mounted() {
    for (var item of this.localValue) {
      if (item.tagId == "" || item.tagId == null || item.tagId == undefined)
        continue;

      await this.loadKeys(item.tagId);
    }
  },

  async created() {
    this.selectableTags = await this.getTagsWithInformation();
  },

  watch: {
    value: {
      async handler(val) {
        for (var item of val) {
          if (item.tagId == "" || item.tagId == null || item.tagId == undefined)
            continue;

          await this.loadKeys(item.tagId);
        }
      },
    },
  },
};
</script>
